/**
 * Chat provider: creates the {@link ChatEngine} over the real transport
 * (blind relay + mailbox + directory of `apps/signaling`), wires it to the
 * local-first persistence and exposes a SolidJS reactive layer (conversations,
 * messages, actions). One polling loop collects the mailbox; another prunes ephemerals.
 */

import {
  type Accessor,
  createContext,
  createSignal,
  onCleanup,
  onMount,
  type ParentComponent,
  useContext,
} from "solid-js";
import { DirectoryClient, MailboxClient } from "@susurro/protocol";

import { type AttachmentInput, ChatEngine } from "@/lib/chat/engine";
import { exportEncryptedBackup } from "@/lib/chat/backup";
import { type CallInfo, CallManager, type CallMediaKind, type CallState } from "@/lib/chat/call";
import { iceServersFromConfig } from "@/lib/chat/ice";
import { P2PManager } from "@/lib/chat/p2p";
import { getOrCreateDeviceId, loadState, saveState } from "@/lib/chat/persistence";
import type { ChatMessage, Conversation } from "@/lib/chat/types";

interface ChatContextValue {
  ready: Accessor<boolean>;
  deviceId: Accessor<string>;
  conversations: Accessor<Conversation[]>;
  getConversation: (id: string) => Conversation | undefined;
  messagesFor: (conversationId: string) => ChatMessage[];
  startDirect: (peerDeviceId: string, title?: string) => Promise<Conversation>;
  createGroup: (title: string, memberDeviceIds: string[]) => Promise<Conversation>;
  sendText: (conversationId: string, text: string, expiresInSecs?: number) => Promise<void>;
  /** Attaches and sends an E2E encrypted binary (file / voice / photo / video). */
  sendAttachment: (
    conversationId: string,
    input: AttachmentInput,
    expiresInSecs?: number,
  ) => Promise<void>;
  /** Blob URL (materialized on demand) of an already decrypted attachment, or undefined. */
  attachmentUrl: (attachId: string, mime: string) => string | undefined;
  safetyNumber: (conversationId: string) => Promise<string | undefined>;
  /** Multi-device: primary sends the encrypted history to a new device. */
  linkDevice: (targetDeviceId: string) => Promise<void>;
  /** This device (new) accepts being linked and receiving the history. */
  enableLinking: () => void;
  linkingEnabled: Accessor<boolean>;
  // --- P2P-first transport (direct DataChannel, blind relay fallback) ---
  /** Tries (best-effort, with throttle) to open the direct channel with a peer. */
  ensureP2p: (peerDeviceId: string) => void;
  /** Is there a direct channel open with that peer? (reactive, for the badge). */
  p2pConnected: (peerDeviceId: string) => boolean;
  /** Backup E2E: encrypts ALL local state with the user's passphrase. */
  exportBackup: (passphrase: string) => Promise<string>;
  // --- Voice/video calls (WebRTC signaled E2E) ---
  callState: Accessor<CallState>;
  callInfo: Accessor<CallInfo | undefined>;
  localStream: Accessor<MediaStream | undefined>;
  remoteStream: Accessor<MediaStream | undefined>;
  startCall: (peerDeviceId: string, media: CallMediaKind) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => Promise<void>;
  hangupCall: () => Promise<void>;
  toggleMute: () => void;
  toggleCamera: () => void;
}

const ChatContext = createContext<ChatContextValue>();

export function useChat(): ChatContextValue {
  const ctx = useContext(ChatContext);
  if (!ctx) {
    throw new Error("useChat must be used within <ChatProvider>");
  }
  return ctx;
}

const POLL_INTERVAL_MS = 1500;
/** During a call we poll faster so that offer/answer/ICE flow. */
const CALL_POLL_MS = 350;
const PRUNE_INTERVAL_MS = 1000;

export const ChatProvider: ParentComponent = (props) => {
  const [ready, setReady] = createSignal(false);
  const [deviceId, setDeviceId] = createSignal("");
  // Version counter: incremented on every engine change so that the
  // derived views (conversations/messagesFor) re-evaluate reactively.
  const [tick, setTick] = createSignal(0);
  const [callState, setCallState] = createSignal<CallState>("idle");
  const [callInfo, setCallInfo] = createSignal<CallInfo | undefined>();
  const [localStream, setLocalStream] = createSignal<MediaStream | undefined>();
  const [remoteStream, setRemoteStream] = createSignal<MediaStream | undefined>();
  let engine: ChatEngine | undefined;
  let call: CallManager | undefined;
  let p2p: P2PManager | undefined;
  // Peers with a direct P2P channel open (for the transport badge).
  const [p2pPeers, setP2pPeers] = createSignal<ReadonlySet<string>>(new Set<string>());
  // Cache attachId → blob URL (created on demand from the engine's bytes).
  const objectUrls = new Map<string, string>();

  const bump = () => {
    if (engine) {
      saveState(engine.persistedState);
    }
    setTick((v) => v + 1);
  };

  onCleanup(() => {
    for (const url of objectUrls.values()) {
      URL.revokeObjectURL(url);
    }
    objectUrls.clear();
  });

  onMount(() => {
    void (async () => {
      const id = getOrCreateDeviceId();
      const state = loadState(id) ?? (await ChatEngine.createInitialState(id));
      const space = "";
      // Origin of the signaling relay (`apps/signaling`). In dev/e2e it is "" →
      // same-origin and the Vite proxy forwards /api/* to 127.0.0.1:16388. In prod the web
      // (Cloudflare Pages) and the relay (k3s) live on different HOSTS: the deploy bakes in
      // `window.__APP_CONFIG__.p2pSignalingUrl` (dist/config.js) with the relay host.
      const relayBase = window.__APP_CONFIG__?.p2pSignalingUrl ?? "";
      const mailbox = new MailboxClient(relayBase, id, space);
      const directory = new DirectoryClient(relayBase, space);
      engine = new ChatEngine(state, { mailbox, directory, onChange: bump });
      setDeviceId(id);
      saveState(state);

      // CallManager: WebRTC signaling travels through the ratchet (blind relay);
      // the media (DTLS-SRTP) flows P2P. No STUN/TURN by default (privacy);
      // an OWN TURN is enabled explicitly behind the local flag
      // `susurro:turn` (see lib/chat/ice.ts); validating it for real is manual.
      const cm = new CallManager({
        selfDeviceId: id,
        send: (peer, signal) => engine!.sendCallSignal(peer, signal),
        createPeerConnection: () => new RTCPeerConnection({ iceServers: iceServersFromConfig() }),
        getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
        onStateChange: (s, info) => {
          setCallState(s);
          setCallInfo({ ...info });
          if (s === "idle") {
            setLocalStream(undefined);
            setRemoteStream(undefined);
          }
        },
        onLocalStream: (s) => setLocalStream(s),
        onRemoteStream: (s) => setRemoteStream(s),
      });
      engine.setCallHandler((peer, signal) => void cm.handleSignal(peer, signal));
      call = cm;

      // P2P-first transport: direct DataChannel negotiated through the ratchet
      // (blind relay: never sees the SDP). With an open channel, the envelopes travel
      // direct and the relay does not even see the ciphertext; with no channel, mailbox.
      const pm = new P2PManager({
        selfDeviceId: id,
        sendSignal: (peer, signal) => engine!.sendP2pSignal(peer, signal),
        onEnvelope: (envelope) => void engine!.deliverP2pEnvelope(envelope),
        createPeerConnection: () => new RTCPeerConnection({ iceServers: iceServersFromConfig() }),
        onStateChange: (peer, connected) => {
          setP2pPeers((prev) => {
            const next = new Set(prev);
            if (connected) {
              next.add(peer);
            } else {
              next.delete(peer);
            }
            return next;
          });
        },
      });
      engine.setP2pHandler((peer, signal) => void pm.handleSignal(peer, signal));
      engine.setP2pSender((peer, envelope) => pm.trySend(peer, envelope));
      p2p = pm;

      try {
        await engine.boot();
      } catch (err) {
        console.warn("[chat] boot (relay no disponible?)", err);
      }
      setReady(true);
      setTick((v) => v + 1);

      // Adaptive polling loop (one at a time, no overlap): faster
      // during a call so that signaling is not delayed.
      let stopped = false;
      let pollTimer: ReturnType<typeof setTimeout> | undefined;
      const scheduleNext = () => {
        if (stopped) {
          return;
        }
        const delay = callState() === "idle" ? POLL_INTERVAL_MS : CALL_POLL_MS;
        pollTimer = setTimeout(() => {
          void engine
            ?.poll()
            .catch((err) => console.warn("[chat] poll", err))
            .finally(scheduleNext);
        }, delay);
      };
      const prune = setInterval(() => engine?.pruneExpired(), PRUNE_INTERVAL_MS);
      // Immediate poll so as not to wait for the first interval.
      void engine.poll().catch(() => undefined);
      scheduleNext();
      onCleanup(() => {
        stopped = true;
        if (pollTimer) {
          clearTimeout(pollTimer);
        }
        clearInterval(prune);
        pm.closeAll();
        void cm.hangup().catch(() => undefined);
      });
    })();
  });

  // Shallow COPIES are returned so that SolidJS's `<For>` re-renders
  // rows whose mutation is in-place (e.g. `status: sent→delivered`): with the
  // same object references, `For` (keyed by reference) would not re-evaluate.
  const conversations: Accessor<Conversation[]> = () => {
    tick();
    return engine ? engine.conversations().map((c) => ({ ...c })) : [];
  };

  const value: ChatContextValue = {
    ready,
    deviceId,
    conversations,
    getConversation: (id) => {
      tick();
      const found = engine?.conversations().find((c) => c.id === id);
      return found ? { ...found } : undefined;
    },
    messagesFor: (conversationId) => {
      tick();
      return engine ? engine.messagesFor(conversationId).map((m) => ({ ...m })) : [];
    },
    startDirect: (peer, title) => mustEngine(engine).startDirectConversation(peer, title),
    createGroup: (title, members) => mustEngine(engine).createGroup(title, members),
    sendText: (id, text, expiresInSecs) => mustEngine(engine).sendText(id, text, expiresInSecs),
    sendAttachment: (id, input, expiresInSecs) =>
      mustEngine(engine).sendAttachment(id, input, expiresInSecs),
    attachmentUrl: (attachId, mime) => {
      tick(); // reactivo: recomputa cuando el adjunto pasa a "ready"
      const cached = objectUrls.get(attachId);
      if (cached) {
        return cached;
      }
      const bytes = engine?.attachmentBytesFor(attachId);
      if (!bytes) {
        return undefined;
      }
      const buffer = new Uint8Array(bytes.length);
      buffer.set(bytes);
      const url = URL.createObjectURL(new Blob([buffer.buffer], { type: mime }));
      objectUrls.set(attachId, url);
      return url;
    },
    safetyNumber: (id) => mustEngine(engine).safetyNumberFor(id),
    linkDevice: (target) => mustEngine(engine).linkDevice(target),
    enableLinking: () => {
      mustEngine(engine).enableLinking();
      setTick((v) => v + 1);
    },
    linkingEnabled: () => {
      tick();
      return engine?.linkingEnabled ?? false;
    },
    ensureP2p: (peer) => {
      // Only with the ratchet session already established: negotiating P2P never opens an
      // X3DH session (avoids glare) nor burns the throttle ahead of time.
      if (engine?.hasSessionWith(peer)) {
        void p2p?.ensureLink(peer);
      }
    },
    p2pConnected: (peer) => p2pPeers().has(peer),
    exportBackup: (passphrase) =>
      exportEncryptedBackup(mustEngine(engine).persistedState, passphrase),
    callState,
    callInfo,
    localStream,
    remoteStream,
    startCall: (peer, media) => mustCall(call).startCall(peer, media),
    acceptCall: () => mustCall(call).accept(),
    rejectCall: () => mustCall(call).reject(),
    hangupCall: () => mustCall(call).hangup(),
    toggleMute: () => {
      mustCall(call).toggleMute();
    },
    toggleCamera: () => {
      mustCall(call).toggleCamera();
    },
  };

  return <ChatContext.Provider value={value}>{props.children}</ChatContext.Provider>;
};

function mustEngine(engine: ChatEngine | undefined): ChatEngine {
  if (!engine) {
    throw new Error("chat: engine not yet initialized");
  }
  return engine;
}

function mustCall(call: CallManager | undefined): CallManager {
  if (!call) {
    throw new Error("chat: call manager not yet initialized");
  }
  return call;
}
